import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";

const TYPES = [
  { v: "individual", label: "Individual buyer", body: "I'm buying office kit for myself or a small setup." },
  { v: "business", label: "Business / office", body: "I'm a business selling, clearing or buying office furniture." },
  { v: "removal_company", label: "Removal company", body: "I offer commercial removals, clearance or recycling." },
];

export default function Register() {
  const [params] = useSearchParams();
  const initialType = params.get("type") === "removal" ? "removal_company" : "individual";
  const [f, setF] = useState({
    email: "", password: "", name: "", phone: "", company_name: "",
    user_type: initialType,
  });
  const [loading, setLoading] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const { register, setUser } = useAuth();
  const nav = useNavigate();
  const googleBtnRef = useRef(null);

  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!termsAgreed) {
      toast.error("Please agree to the Terms & Conditions and Privacy Policy to continue");
      return;
    }
    setLoading(true);
    try {
      await register({ ...f, marketing_consent: marketingConsent });
      toast.success("Account created");
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  const handleGoogleCredential = useCallback(async (response) => {
    try {
      const { data } = await api.post("/auth/google", { id_token: response.credential });
      localStorage.setItem("fe_token", data.token);
      setUser(data.user);
      toast.success("Welcome");
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Google sign-in failed");
    }
  }, [nav, setUser]);

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId || !window.google || !googleBtnRef.current) return;
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: handleGoogleCredential,
    });
    window.google.accounts.id.renderButton(googleBtnRef.current, {
      theme: "outline",
      size: "large",
      width: 360,
      text: "continue_with",
    });
  }, [handleGoogleCredential]);

  return (
    <div className="mx-auto max-w-2xl px-6 py-16">
      <div className="overline text-safety">Join Furniture Exchange</div>
      <h1 className="mt-2 font-display font-black text-4xl tracking-tighter text-forest">Create your account</h1>

      <form onSubmit={submit} className="mt-8 border border-border bg-white p-8 space-y-6">
        <div>
          <div className="overline text-muted-foreground mb-3">I am\u2026</div>
          <RadioGroup value={f.user_type} onValueChange={set("user_type")} className="grid grid-cols-1 gap-2">
            {TYPES.map((t) => (
              <Label
                key={t.v}
                htmlFor={t.v}
                className={`flex items-start gap-3 border p-4 cursor-pointer transition-colors duration-200 ${f.user_type === t.v ? "border-forest bg-sage-light" : "border-border"}`}
                data-testid={`register-type-${t.v}`}
              >
                <RadioGroupItem id={t.v} value={t.v} className="mt-1" />
                <div>
                  <div className="font-display font-medium text-base">{t.label}</div>
                  <div className="text-xs text-muted-foreground mt-1">{t.body}</div>
                </div>
              </Label>
            ))}
          </RadioGroup>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <div className="overline text-muted-foreground mb-2">Full name *</div>
            <Input required value={f.name} onChange={set("name")} data-testid="register-name" className="rounded-none h-12" />
          </div>
          {(f.user_type === "business" || f.user_type === "removal_company") && (
            <div>
              <div className="overline text-muted-foreground mb-2">Company name</div>
              <Input value={f.company_name} onChange={set("company_name")} data-testid="register-company" className="rounded-none h-12" />
            </div>
          )}
          <div>
            <div className="overline text-muted-foreground mb-2">Email *</div>
            <Input required type="email" value={f.email} onChange={set("email")} data-testid="register-email" className="rounded-none h-12" />
          </div>
          <div>
            <div className="overline text-muted-foreground mb-2">Phone</div>
            <Input value={f.phone} onChange={set("phone")} data-testid="register-phone" className="rounded-none h-12" />
          </div>
          <div className="sm:col-span-2">
            <div className="overline text-muted-foreground mb-2">Password *</div>
            <Input required type="password" minLength={6} value={f.password} onChange={set("password")} data-testid="register-password" className="rounded-none h-12" />
          </div>
        </div>

        <div className="space-y-3 text-sm">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              required
              checked={termsAgreed}
              onChange={(e) => setTermsAgreed(e.target.checked)}
              data-testid="register-terms"
              className="mt-1 h-4 w-4"
            />
            <span>
              I agree to the{" "}
              <Link to="/terms" className="text-safety hover:underline" target="_blank">Terms &amp; Conditions</Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-safety hover:underline" target="_blank">Privacy Policy</Link>. *
            </span>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
              data-testid="register-marketing"
              className="mt-1 h-4 w-4"
            />
            <span>
              I'm happy to receive offers and updates from Furniture Exchange and our parent
              company, Corporate Office Furniture Ltd. You can unsubscribe at any time.
            </span>
          </label>
        </div>

        <Button type="submit" disabled={loading || !termsAgreed} data-testid="register-submit" className="w-full rounded-none bg-forest hover:bg-forest-800 text-white h-12">
          {loading ? "Creating\u2026" : "Create account"}
        </Button>

        <div className="relative py-2">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
          <div className="relative flex justify-center text-xs overline text-muted-foreground bg-white px-3 mx-auto w-fit">or</div>
        </div>
        <div ref={googleBtnRef} className="flex justify-center" data-testid="register-google" />
      </form>

      <div className="mt-6 text-sm text-muted-foreground text-center">
        Already registered?{" "}
        <Link to="/login" className="text-forest hover:text-safety" data-testid="register-to-login">Log in</Link>
      </div>
    </div>
  );
}
